import React, { useEffect, useState } from 'react';

import {
   Box,
   CircularProgress,
   Divider,
   Typography,
   Switch,
   FormControl,
   FormControlLabel,
   FormHelperText,
} from '@mui/material';
import { withFirebase } from '../../Firebase';
import CheckingInPanel from './checkingInPanel';
import AlertToggle from './alertToggle';
import HostTypeSelector from './hostTypeSelector';
import LandlordVisitor from './landlordVisitor';
import TenantVisitor from './tenantVisitor';
import TenantImageUpload from './tenantImageUpload';
import HostAutocompleteToggle from './hostAutocompleteToggle';
import Customisations from './customisations';

const VisitorsPanel = ({ expanded, ...props }) => {
   const [visitorsEnabled, setVisitorsEnabled] = useState(false);
   const [companyFeatureEnabled, setCompanyFeatureEnabled] = useState(null);
   const [locationToggleSaving, setLocationToggleSaving] = useState(false);
   const [role, setRole] = useState('Loading');

   useEffect(() => {
      setVisitorsEnabled(!!props.location.visitorsEnabled);
      setRole(props.location.visitorHostType || 'Default');
   }, [props.location]);

   useEffect(() => {
      let isMounted = true;
      const fetchCompanyFeature = async () => {
         if (!props.cid) return;
         try {
            const co = await props.firebase.company(props.cid).get();
            if (!isMounted) return;
            setCompanyFeatureEnabled(!!co.data()?.features?.visitors);
         } catch (e) {
            console.warn(e);
            if (isMounted) setCompanyFeatureEnabled(false);
         }
      };
      fetchCompanyFeature();
      return () => {
         isMounted = false;
      };
   }, [props.cid]);

   async function toggleLocationVisitors(enabled) {
      setLocationToggleSaving(true);
      try {
         await props.firebase.setVisitorsEnabled(props.locationKey, enabled);
         setVisitorsEnabled(enabled);
         if (enabled) {
            setRole(props.location.visitorHostType || 'Default');
         }
      } catch (e) {
         console.warn(e);
      }
      setLocationToggleSaving(false);
   }
   if (expanded) {
      if (companyFeatureEnabled === null) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
               <CircularProgress />
            </Box>
         );
      }
      if (!companyFeatureEnabled) {
         return (
            <Box>
               <Typography variant="body1">
                  Visitors is a premium feature that must be enabled at the company
                  level. Please go to the Company tab to enable this feature.
               </Typography>
            </Box>
         );
      }
      return (
         <Box>
            <FormControl>
               <FormControlLabel
                  label={
                     visitorsEnabled
                        ? 'Visitor Management is enabled for this location'
                        : 'Visitor Management is disabled for this location'
                  }
                  control={
                     <Switch
                        disabled={locationToggleSaving}
                        checked={visitorsEnabled}
                        onChange={(e) => toggleLocationVisitors(e.target.checked)}
                     />
                  }
               />
               <FormHelperText>
                  Turn off to remove the kiosk and visitor sign-in flow at this location
                  only. Other locations are unaffected.
               </FormHelperText>
            </FormControl>
            <Divider sx={{ mt: 1, mb: 1 }} />
            {!visitorsEnabled ? null : (
               <Box>
                  <Box>
                     <HostTypeSelector
                        role={role}
                        setRole={setRole}
                        cid={props.location.cid}
                        lid={props.locationKey}
                        location={props.location}
                     />
                     <Divider sx={{ mt: 1, mb: 1 }} />
                  </Box>
                  {role === 'Default' ? (
                     <Box>
                        <CheckingInPanel
                           lid={props.locationKey}
                           locationName={props.location.name}
                           expanded={props.expanded}
                           visitorsEnabled={visitorsEnabled}
                        />
                        <Divider sx={{ mt: 1, mb: 1 }} />
                        <AlertToggle
                           expanded={props.expanded}
                           visitorsEnabled={visitorsEnabled}
                           location={props.location}
                           locationKey={props.locationKey}
                           cid={props.location.cid}
                        />
                        <Divider sx={{ mt: 1, mb: 1 }} />
                        <Customisations lid={props.locationKey} mode="default" />
                     </Box>
                  ) : role === 'Landlord' ? (
                     <Box>
                        <LandlordVisitor
                           lid={props.locationKey}
                           cid={props.location.cid}
                           location={props.location}
                        />
                     </Box>
                  ) : role === 'Tenant' ? (
                     <Box>
                        <TenantVisitor
                           lid={props.locationKey}
                           cid={props.location.cid}
                           location={props.location}
                        />
                     </Box>
                  ) : (
                     <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                     </Box>
                  )}
                  <Box>
                     <Divider sx={{ mt: 2, mb: 2 }} />
                     <Typography variant="h6">Host Autocomplete Settings</Typography>

                     <HostAutocompleteToggle
                        lid={props.locationKey}
                        location={props.location}
                     />
                  </Box>
                  <Box>
                     <Divider sx={{ mt: 2, mb: 2 }} />
                     <Typography variant="h6">Upload your logo</Typography>
                     <TenantImageUpload
                        lid={props.locationKey}
                        location={props.location}
                     />
                  </Box>
               </Box>
            )}
         </Box>
      );
   } else return null;
};

export default withFirebase(VisitorsPanel);
